import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpContext, HttpParams } from "@angular/common/http";

import { environment } from 'src/environments/environment';
import { CatalogItem, IEdge, Item } from "~shared/types/geometry";
import { Layout, LayoutFit, LoadedLayout, SavedLayout } from "~shared/types/layout.types";
import { ItemDimension } from "~shared/types/dimension.types";
import { CreateItemDto, FrameDivisionDto, FrameOffsetDto, ItemDto, ResizeFrameDto } from "~modules/configurator/store/items/item.dto.types";
import { SKIP_AUTH } from '~core/interceptors/header.interceptor';

import { RegenerateAccepted } from '~core/services/bom-regenerate.service';

import { CreateConnectionHardwareDTO, UpdateConnectionDTO, UpdateConnectionHardwareVariantDTO } from './dto/article.dto'

@Injectable({ providedIn: 'root' })
export class ArticleService {
	articleUrl: string = environment.articleServiceUrl;

	constructor(private http: HttpClient) {}

	/**
	 * The dimensions of an outline set with the rules that hold for them, for an item that
	 * is still being drawn. Nothing is stored.
	 */
	getOutlineSetDimension(outlineSetLabel: string, dimension: unknown): Observable<ItemDimension> {
		return this.http.post<ItemDimension>(`${this.articleUrl}outlineset/dimension`, { outlineSetLabel, dimension });
	}

	createItem(partId: string, item: CreateItemDto): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/cabinet`, item);
	}

	duplicateItem(partId: string, itemId: string): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}`, {});
	}

	/**
	 * `clearFrameIds` is the retry after a 409 naming frames whose layout cannot
	 * take their new size: it gives up what is inside exactly those frames.
	 */
	updateItemDimension(
		itemId: string, partId: string, dimension, clearFrameIds: string[] = []
	): Observable<ItemDto> {
		let params = new HttpParams();
		clearFrameIds.forEach(id => params = params.append('clearFrameIds', id));
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/dimension`, dimension, { params });
	}

	splitFrame(partId: string, itemId: string, frameId: string, clearFrameIds: string[] = []): Observable<ItemDto> {
		let params = new HttpParams();
		clearFrameIds.forEach(id => params = params.append('clearFrameIds', id));
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/frame/${frameId}/split`, {}, { params });
	}

	stackFrame(partId: string, itemId: string, frameId: string): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/frame/${frameId}/stack`, {});
	}

	removeFrame(partId: string, itemId: string, frameId: string): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/frame/${frameId}`);
	}

	resizeFrame(partId: string, itemId: string, frameId: string, size: ResizeFrameDto): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/frame/${frameId}/dimension`, size);
	}

	setFrameOffset(partId: string, itemId: string, frameId: string, offset: FrameOffsetDto): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/frame/${frameId}/offset`, offset);
	}

	setFrameDivision(partId: string, itemId: string, frameZoneId: string, division: FrameDivisionDto): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/frame-division/${frameZoneId}`, division);
	}

	releaseFrameDimension(
		partId: string, itemId: string, frameId: string, measure: 'W' | 'H' | 'D', clearFrameIds: string[] = []
	): Observable<ItemDto> {
		let params = new HttpParams();
		clearFrameIds.forEach(id => params = params.append('clearFrameIds', id));
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/frame/${frameId}/dimension/${measure}`, { params });
	}

	updateItem(itemId: string, partId: string, body): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}`, body);
	}

	getItem(itemId: string, partId?: string): Observable<ItemDto[]> {
		let params = new HttpParams().set('id', itemId);
		if (partId) {
			params = params.set('part_id', partId);
		}
		return this.http
			.get<ItemDto[]>(`${this.articleUrl}item`, {
				params,
				context: new HttpContext().set(SKIP_AUTH, true)
			});
	}

	updateItemName(itemId: string, partId: string, name): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}`, { name });
	}

	addItemFiller(itemId: string, partId: string, fillerType: string, baseType: string, outlineFaceIdx: number): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/filler`, {
				fillerType,
				baseType,
				outlineFaceIdx
			});
	}

	removeItemFiller(itemId: string, partId: string, outlineFaceIdx: number): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/filler/${outlineFaceIdx}`);
	}

	getItemsByPartIds(partIds: string[]): Observable<ItemDto[]> {
		return this.http
			.get<ItemDto[]>(this.articleUrl + 'item', {
				params: {
					part_id: partIds
				},
				context: new HttpContext().set(SKIP_AUTH, true)
			});
	}

	deleteItem(partId: string, itemId: string): Observable<void> {
		const url = this.articleUrl + 'part/' + partId + '/item/' + itemId;
		return this.http
			.delete<void>(url);
	}
	createCatalogItem(partId: string, itemId: string, values, accountId?: string): Observable<CatalogItem> {
		if (accountId) {
			return this.http
				.post<CatalogItem>(`${this.articleUrl}catalog/account/${accountId}/part/${partId}/item/${itemId}`, values);
		}

		return this.http
			.post<CatalogItem>(`${this.articleUrl}catalog/part/${partId}/item/${itemId}`, values);
	}

	updateCatalogItem(catalogItemId: string, values): Observable<CatalogItem> {
		return this.http
			.patch<CatalogItem>(`${this.articleUrl}catalog/${catalogItemId}`, values);
	}

	toggleCatalogItemFavourite(catalogItemId: string, accountId: string): Observable<CatalogItem> {
		return this.http
			.patch<CatalogItem>(`${this.articleUrl}catalog/${catalogItemId}/account/${accountId}/favourite`, {});
	}

	deleteCatalogItem(catalogItemId: string, accountId?: string): Observable<void> {
		if (accountId) {
			return this.http
				.delete<void>(`${this.articleUrl}catalog/${catalogItemId}/account/${accountId}`);
		}

		return this.http
			.delete<void>(`${this.articleUrl}catalog/${catalogItemId}`);
	}

	addCatalogItemToPart(partId: string, catalogItemId: string): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/catalog/${catalogItemId}`, {});
	}

	getCatalogItems(accountId: string): Observable<CatalogItem[]> {
		return combineLatest([
			this.http.get<CatalogItem[]>(`${this.articleUrl}catalog/account/${accountId}/itemtype/CABINET`),
			// this.http.get<CatalogItem[]>(`${this.articleUrl}catalog/itemtype/CABINET`),
			of([]),
		])
			.pipe(
				map(([accountCatalogItem, genericCatalogItems]) => [...accountCatalogItem, ...genericCatalogItems])
			);
	}

	/** The layouts of an account and the ones of every account, with the account's favourites. */
	getLayouts(accountId: string): Observable<Layout[]> {
		return this.http.get<Layout[]>(`${this.articleUrl}layout/account/${accountId}`);
	}

	/** Keep what stands in a frame as a layout of the account, or without one of every account. */
	saveLayout(accountId: string | null, partId: string, itemId: string, frameId: string): Observable<Layout> {
		const owner = accountId ? `account/${accountId}/` : '';
		return this.http
			.post<Layout>(`${this.articleUrl}layout/${owner}part/${partId}/item/${itemId}/frame/${frameId}`, {});
	}

	/** A layout of every account is deleted without an account. */
	deleteLayout(layoutId: string, accountId: string | null): Observable<void> {
		const owner = accountId ? `/account/${accountId}` : '';
		return this.http.delete<void>(`${this.articleUrl}layout/${layoutId}${owner}`);
	}

	starLayout(accountId: string, layoutId: string): Observable<void> {
		return this.http.put<void>(`${this.articleUrl}layout/account/${accountId}/favourite/${layoutId}`, {});
	}

	unstarLayout(accountId: string, layoutId: string): Observable<void> {
		return this.http.delete<void>(`${this.articleUrl}layout/account/${accountId}/favourite/${layoutId}`);
	}

	/** The account's favourites in this order; the list names all of them. */
	orderFavourites(accountId: string, layoutIds: string[]): Observable<void> {
		return this.http.put<void>(`${this.articleUrl}layout/account/${accountId}/favourites`, { layoutIds });
	}

	/** These layouts, not favourites, in this order: the first gets `offset`, the next one more. */
	resequenceLayouts(accountId: string, layoutIds: string[], offset: number): Observable<void> {
		return this.http.put<void>(`${this.articleUrl}layout/account/${accountId}/resequence`, { layoutIds, offset });
	}

	resequenceGenericLayouts(layoutIds: string[], offset: number): Observable<void> {
		return this.http.put<void>(`${this.articleUrl}layout/generic/resequence`, { layoutIds, offset });
	}

	/** The layout the account sees that already holds what stands in the frame. */
	getSavedLayout(accountId: string, partId: string, itemId: string, frameId: string): Observable<SavedLayout> {
		return this.http.get<SavedLayout>(
			`${this.articleUrl}layout/account/${accountId}/part/${partId}/item/${itemId}/frame/${frameId}/saved`
		);
	}

	/** Of these layouts, at most ten, the ones that fit the frame, each with a preview. */
	getLayoutFits(accountId: string, partId: string, itemId: string, frameId: string, layoutIds: string[]): Observable<LayoutFit[]> {
		let params = new HttpParams();
		layoutIds.forEach(id => params = params.append('layoutIds', id));
		return this.http.get<LayoutFit[]>(
			`${this.articleUrl}layout/account/${accountId}/part/${partId}/item/${itemId}/frame/${frameId}/fits`, { params }
		);
	}

	/** Put a layout in an empty frame; the answer names the fronts the frame could not keep. */
	loadLayout(accountId: string, partId: string, itemId: string, frameId: string, layoutId: string): Observable<LoadedLayout> {
		return this.http.post<LoadedLayout>(
			`${this.articleUrl}layout/account/${accountId}/part/${partId}/item/${itemId}/frame/${frameId}/load/${layoutId}`, {}
		);
	}

	getCatalogItem(catalogItemId: string): Observable<CatalogItem> {
		return this.http.get<CatalogItem>(`${this.articleUrl}catalog/${catalogItemId}`);
	}

	createArticleInArticleZone(
		partId: string,
		itemId: string,
		articleZoneId: string,
		articleType: string,
		body
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/articlezone/${articleZoneId}/article/${articleType}`, body);
	}

	createDivider(
		partId: string,
		itemId: string,
		articleZoneId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/divider/articlezone/${articleZoneId}`, body);
	}

	createShelf(
		partId: string,
		itemId: string,
		articleZoneId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/shelves/articlezone/${articleZoneId}`, body);
	}

	createDoor(
		partId: string,
		itemId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/door`, body);
	}

	mergeFronts(partId: string, itemId: string, frontIds: string[]): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/front/merge`, { frontIds });
	}

	splitFront(partId: string, itemId: string, frontId: string): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/front/${frontId}/split`, {});
	}

	createPanelCutout(
		partId: string,
		itemId: string,
		panelId: string,
		cutoutType: string
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/cutout/${cutoutType}`, {});
	}

	deletePanelCutout(
		partId: string,
		itemId: string,
		panelId: string,
		cutoutType: string
	): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/cutout/${cutoutType}`, {});
	}

	updatePanelCutout(
		partId: string,
		itemId: string,
		panelId: string,
		cutoutId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/cutout/${cutoutId}`, body);
	}

	updatePanel(
		partId: string,
		itemId: string,
		panelId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}`, body);
	}

	/**
	 * Patch one core edge. The body is a partial edge: only the fields present are
	 * applied, so `{ extend }`, `{ oversize }` and `{ edgeband }` all use this route.
	 */
	updatePanelEdge(
		partId: string,
		itemId: string,
		panelId: string,
		edgeIndex: number,
		patch: Partial<IEdge>
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(
				`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/edge/${edgeIndex}`,
				patch
			);
	}

	updateArticle(
		partId: string,
		itemId: string,
		articleId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/article/${articleId}`, body);
	}

	updateHardware(
		partId: string,
		itemId: string,
		hardwareId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/hardware/${hardwareId}`, body);
	}

	createConnectionHardware(
		partId: string,
		itemId: string,
		panelId: string,
		connectionType: string,
		body: CreateConnectionHardwareDTO
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/connection/${connectionType}/hardware`, body);
	}

	updateConnection(
		partId: string,
		itemId: string,
		panelId: string,
		connectionType: string,
		body: UpdateConnectionDTO
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/connection/${connectionType}`, body);
	}

	updateConnectionHardwareVariant(
		partId: string,
		itemId: string,
		panelId: string,
		connectionType: string,
		body: UpdateConnectionHardwareVariantDTO
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/connection/${connectionType}/hardware/variant`, body);
	}

	updateDivider(
		partId: string,
		itemId: string,
		articleId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/divider/${articleId}`, body);
	}

	updateShelf(
		partId: string,
		itemId: string,
		articleId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/shelves/${articleId}`, body);
	}

	updatePanelParameter(
		partId: string,
		itemId: string,
		panelId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/parameter`, body);
	}

	updateArticleParameter(
		partId: string,
		itemId: string,
		articleId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/article/${articleId}/parameter`, body);
	}

	updateFillerConstruction(
		partId: string,
		itemId: string,
		outlineFaceIdx: number,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/filler/${outlineFaceIdx}`, body);
	}

	deleteArticle(
		partId: string,
		itemId: string,
		articleId: string
	): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/article/${articleId}`);
	}

	deletePanel(
		partId: string,
		itemId: string,
		panelId: string
	): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}`);
	}

	restorePanel(
		partId: string,
		itemId: string,
		panelId: string
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/restore`, {});
	}

	flipPanelGrain(
		partId: string,
		itemId: string,
		panelId: string
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/grain/flip`, {});
	}

	unlockPanelGrain(
		partId: string,
		itemId: string,
		panelId: string
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/grain/unlock`, {});
	}

	addHardwareToPanel(
		partId: string,
		itemId: string,
		panelId: string,
		body
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/connection/HANDLE/hardware`, body);
	}

	updatePanelConnection(
		partId: string,
		itemId: string,
		panelId: string,
		connectionType: string,
		body
	): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/connection/${connectionType}`, body);
	}

	deleteHardwareFromPanel(
		partId: string,
		itemId: string,
		panelId: string,
	): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/connection/HANDLE`, {});
	}

	public addCustomisationToPanel(
		partId: string,
		itemId: string,
		panelId: string,
		customisationId: string
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/customisation/${customisationId}`, {});
	}

	public addCustomisationToLayout(
		partId: string,
		itemId: string,
		articleZoneId: string,
		customisationId: string
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/articlezone/${articleZoneId}/customisation/${customisationId}`, {});
	}

	public addCustomisationToItem(
		partId: string,
		itemId: string,
		customisationId: string
	): Observable<ItemDto> {
		return this.http
			.post<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/customisation/${customisationId}`, {});
	}

	public deleteCustomisationFromPanel(
		partId: string,
		itemId: string,
		panelId: string,
		customisationId: string
	): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/customisation/${customisationId}`, {});
	}

	public deleteCustomisationFromLayout(
		partId: string,
		itemId: string,
		articleZoneId: string,
		customisationId: string
	): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/articlezone/${articleZoneId}/customisation/${customisationId}`, {});
	}

	public deleteCustomisationFromItem(
		partId: string,
		itemId: string,
		customisationId: string
	): Observable<ItemDto> {
		return this.http
			.delete<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/customisation/${customisationId}`, {});
	}

	public updatePanelGroupBoard(partId: string, itemId: string, panelGroup: string, body, panelLabel?: string): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(panelLabel ? `${this.articleUrl}part/${partId}/item/${itemId}/board/panelgroup/${panelGroup}/${panelLabel}` : `${this.articleUrl}part/${partId}/item/${itemId}/board/panelgroup/${panelGroup}`, body);
	}

	public recreateBack(partId: string, itemId: string, articleZoneId: string, faceIdx: number): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/articlezone/${articleZoneId}/faceidx/${faceIdx}/recreate`, {});
	}

	public replacePartBoard(partId: string, boardSku: string, body): Observable<string> {
		return this.http
			.put(`${this.articleUrl}part/${partId}/board/${boardSku}`, body, { responseType: 'text' });
	}

	public updatePartPanelGroupBoard(partId: string, panelGroup: string, body, panelLabel?: string): Observable<ItemDto[]> {
		return this.http
			.patch<ItemDto[]>(panelLabel ? `${this.articleUrl}part/${partId}/board/panelgroup/${panelGroup}/${panelLabel}` : `${this.articleUrl}part/${partId}/board/panelgroup/${panelGroup}`, body);
	}

	public updatePanelBoard(partId: string, itemId: string, panelId: string, body): Observable<ItemDto> {
		return this.http
			.patch<ItemDto>(`${this.articleUrl}part/${partId}/item/${itemId}/panel/${panelId}/board`, body);
	}

	/** Queues the rebuild and returns immediately (202); progress arrives over the
	 * websocket effect stream, tracked by BomRegenerateService. */
	public regenerateBomItems(partId: string): Observable<RegenerateAccepted> {
		return this.http
			.post<RegenerateAccepted>(`${this.articleUrl}part/${partId}/bomitems/regenerate`, {});
	}

	public regenerateProjectBomItems(projectId: string): Observable<RegenerateAccepted> {
		return this.http
			.post<RegenerateAccepted>(`${this.articleUrl}project/${projectId}/bomitems/regenerate`, {});
	}
}
